"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ALL_IP_ADMISSION_PERMISSIONS = exports.IpAdmissionPermissions = void 0;
/**
 * Permission catalog for the IP (inpatient) bounded context — ADR-085 Phase 0.
 *
 * Format `<module>:<resource>:<action>`, matching Financial's and Clinical's
 * proven 3-segment convention rather than Patient's/Appointment's mixed
 * 2-/3-segment inconsistency.
 *
 * Before this file, `grep "ADMISSION\|DISCHARGE"` over this directory returned
 * exactly one unrelated hit (`PHARMACY_SALE_IP_BILL`). The IP workflow had
 * **zero** permissions, which the 2026-08-02 audit recorded as C-10 and the
 * 2026-08-05 IP billing audit as IPB-C-12.
 *
 * Modelled per workflow, not as CRUD: an admission is not "created" and
 * "updated" — it is admitted, transferred, advised for discharge, cleared,
 * billed, discharged and exited, and each of those is a distinct authority a
 * hospital grants to a different role. A generic `IP_ADMISSION_UPDATE` would
 * let a ward clerk discharge a patient.
 *
 * NOT included, deliberately: ward- and department-SCOPED variants. ADR-058
 * records that scoped RBAC stays deferred, so a nurse holding
 * `IP_BED_ALLOCATE` today can allocate a bed on any ward. That is an accepted,
 * disclosed risk — not an oversight — and it is why the scoped upgrade is
 * tracked separately rather than half-built here.
 */
exports.IpAdmissionPermissions = {
    /** Every read endpoint: list/get admissions, occupancy, bed board, movement history. */
    IP_ADMISSION_VIEW: 'ip:admission:view',
    /** Admit a patient. Creates the admission, the visit binding and the first occupancy. */
    IP_ADMISSION_ADMIT: 'ip:admission:admit',
    /** Amend non-lifecycle admission attributes (consultant, diagnosis, payer, MLC details). */
    IP_ADMISSION_AMEND: 'ip:admission:amend',
    /**
     * Approve or disable Cost Sheet Provision on an admission — that one yes/no switch and nothing else
     * (desk, 2026-09-30: the front desk needs it). Its own key rather than `IP_ADMISSION_AMEND`, because amend
     * also changes payer, credit status, consultant, department, MLC details and diagnoses, which the front
     * desk was not asked to have. The route accepts EITHER this or amend, so a role that already holds amend
     * (IP Officer, IP User) keeps working with no new grant. `costsheet`, one word: every key here is a strict
     * 3-segment `ip:resource:action` (enforced by `ip-permission-catalog.test.ts`).
     */
    IP_COST_SHEET_PROVISION_MANAGE: 'ip:costsheet:manage',
    /**
     * Cancel an admission. Reachable only from ADMITTED and only with zero ledger rows.
     * Also what is needed to REQUEST a cancellation that an advance blocks from being direct —
     * one action, one permission; the server decides which of the two happens (desk, 2026-09-25).
     */
    IP_ADMISSION_CANCEL: 'ip:admission:cancel',
    /**
     * Approve (or reject) a cancellation request that an advance sent for review — a SEPARATE
     * grant from `IP_ADMISSION_CANCEL` itself, so the same clerk cannot both ask and approve.
     * `cancellation`, not `admission:cancel:approve` — every key in this catalog is a strict
     * 3-segment `ip:resource:action` (enforced by `ip-permission-catalog.test.ts`).
     */
    IP_ADMISSION_CANCEL_APPROVE: 'ip:cancellation:approve',
    /** Allocate a bed to an admission, or release one. */
    IP_BED_ALLOCATE: 'ip:bed:allocate',
    /** Move a patient between beds, rooms, wards or departments. */
    IP_BED_TRANSFER: 'ip:bed:transfer',
    /**
     * Administrative bed status transitions: block, unblock, maintenance,
     * request/complete cleaning. Distinct from allocation because these are
     * facilities operations, not patient movement.
     *
     * ADR-051 — after Phase 0, `Bed.status` is no longer writable through
     * master-data CRUD. `BED_MANAGE` (the master-data permission) no longer
     * carries the authority to set a bed OCCUPIED; only the ADT services do,
     * transactionally, with history.
     */
    IP_BED_STATUS_MANAGE: 'ip:bed:status-manage',
    /** Record discharge advice. ADMITTED -> DISCHARGE_ADVISED. */
    IP_DISCHARGE_ADVISE: 'ip:discharge:advise',
    /** Complete discharge once the bill is finalised. BILL_FINALISED -> DISCHARGED. */
    IP_DISCHARGE_COMPLETE: 'ip:discharge:complete',
    /** Authorise physical exit. DISCHARGED -> EXITED. */
    IP_DISCHARGE_EXIT: 'ip:discharge:exit',
    /**
     * Cancel a discharge inside the 48-hour window and reopen the stay for further
     * entries. DISCHARGED -> ADMITTED. Its own authority, never folded into
     * `IP_DISCHARGE_COMPLETE`: the person who may end a stay is not thereby the
     * person who may un-end one, and this reopens a closed bill's ledger.
     */
    IP_DISCHARGE_CANCEL: 'ip:discharge:cancel',
    // --- ADR-085 Phase 1 — charge ledger ------------------------------------
    /** Read the running charge ledger, its summary and its per-day breakdown. */
    IP_CHARGE_VIEW: 'ip:charge:view',
    /**
     * Post a charge manually. Automated sources (lab, radiology, pharmacy,
     * recurring accrual) call `IpChargeCaptureService` in-process and are NOT
     * gated on this — a background job holds no user's permissions. This gates
     * the human path only.
     */
    IP_CHARGE_CAPTURE: 'ip:charge:capture',
    /** Reverse a captured charge. Separate from capture: correcting a bill is a
     * different authority from raising one. */
    IP_CHARGE_REVERSE: 'ip:charge:reverse',
    // --- ADR-085 Phase 2 — pricing -------------------------------------------
    /**
     * Search the Service Master and resolve a price for a service.
     *
     * Deliberately a READ permission distinct from `IP_CHARGE_CAPTURE`: a clerk
     * who may quote a price to a patient is not necessarily one who may post the
     * charge, and quoting is the far more common action.
     */
    IP_PRICING_RESOLVE: 'ip:pricing:resolve',
    // --- ADR-085 Phase 4 — package billing -----------------------------------
    /** Read package enrolments, utilisation and the redemption ledger. */
    IP_PACKAGE_VIEW: 'ip:package:view',
    /**
     * Bind a package to a stay, or cancel that binding. Distinct from
     * `IP_CHARGE_CAPTURE`: enrolling a package changes who pays for everything
     * that follows, which is a commercial decision rather than a clinical one.
     */
    IP_PACKAGE_MANAGE: 'ip:package:manage',
    // --- ADR-085 Phase 5 — insurance -----------------------------------------
    //
    // Five separate permissions, not one. Approving a pre-authorisation,
    // submitting a claim and settling one are three different authorities held by
    // three different desks in a real hospital: a TPA coordinator raises, a
    // manager approves, and finance settles. Collapsing them into
    // `insurance:manage` would let whoever raises a claim also mark it paid.
    /** Read plans, policies, pre-auths, allocations and claims. */
    IP_INSURANCE_VIEW: 'ip:insurance:view',
    /** Maintain plans, pricing policies, exclusions, waiting periods, networks. */
    IP_INSURANCE_EDIT: 'ip:insurance:edit',
    /** Record an insurer's decision on a pre-authorisation, including enhancement. */
    IP_INSURANCE_PREAUTH_APPROVE: 'ip:insurance:preauth-approve',
    /** Raise and submit a claim. */
    IP_INSURANCE_CLAIM_SUBMIT: 'ip:insurance:claim-submit',
    /** Record money received against a claim. */
    IP_INSURANCE_CLAIM_SETTLE: 'ip:insurance:claim-settle',
    // --- ADR-085 Phase 6 — corporate credit (AR) -----------------------------
    /** Read corporate authorisations, allocations, credit position, aging, statement. */
    IP_CORPORATE_VIEW: 'ip:corporate:view',
    /** Raise an authorisation request against a corporate account. */
    IP_CORPORATE_REQUEST: 'ip:corporate:request',
    /**
     * Approve or enhance a corporate authorisation. Separate from requesting:
     * committing a corporate's credit is a credit-control decision, and the desk
     * that raises the request is not the desk that grants it.
     */
    IP_CORPORATE_APPROVE: 'ip:corporate:approve',
    /** Record settlement against corporate receivables. */
    IP_CORPORATE_SETTLE: 'ip:corporate:settle',
    // --- ADR-085 Phase 7 — discounts -----------------------------------------
    /** Read the discount register, approval chains and pending queue. */
    IP_DISCOUNT_VIEW: 'ip:discount:view',
    /** Raise a discount request. Grants no authority to approve one. */
    IP_DISCOUNT_REQUEST: 'ip:discount:request',
    /**
     * Approve or reject. Separate from requesting for the obvious reason: a clerk
     * who could approve their own request is not an approval workflow. HOW MUCH
     * they may approve is bounded further by the authority matrix.
     */
    IP_DISCOUNT_APPROVE: 'ip:discount:approve',
    /** Maintain reason codes and the approval-ceiling matrix. */
    IP_DISCOUNT_CONFIGURE: 'ip:discount:configure',
    /**
     * Enter a refund against a stay's deposits. Up to the approval threshold
     * (`IP_REFUND_APPROVAL_THRESHOLD_PAISE`, Rs 3,000) it is recorded at once -
     * no approval step, as the desk asked on 2026-09-24: the service recomputes
     * the refundable balance live and refuses anything beyond it. ABOVE the
     * threshold (desk, 2026-10-09: "refund above 3000, Lathamani should approve")
     * this permission only RAISES A REQUEST; it moves no money until someone
     * holding `IP_REFUND_APPROVE` approves it.
     */
    IP_REFUND_CREATE: 'ip:refund:create',
    /**
     * Approve or reject a refund request above the threshold. Separate from
     * entering one for the obvious reason, and an approver can never approve a
     * request they entered themselves. Approving posts the refund (the voucher
     * is raised in the approval, under the approver's name).
     */
    IP_REFUND_APPROVE: 'ip:refund:approve',
    // --- ADR-085 Phase 8 — interim bills --------------------------------------
    /** View and reprint interim bills, and compare snapshots. */
    IP_INTERIM_BILL_VIEW: 'ip:interim-bill:view',
    /** Generate a new snapshot, or cancel one. */
    IP_INTERIM_BILL_GENERATE: 'ip:interim-bill:generate',
    // --- ADR-085 Phase 9 — final settlement -----------------------------------
    /** View settlements, run the pre-settlement validation, verify a fingerprint. */
    IP_SETTLEMENT_VIEW: 'ip:settlement:view',
    /** Bind and adjust deposits against a stay. */
    IP_SETTLEMENT_DEPOSIT_MANAGE: 'ip:settlement:deposit-manage',
    /**
     * Settle a stay. This is the operation that creates the legal invoice, so it
     * is deliberately the narrowest permission in the IP family.
     */
    IP_SETTLEMENT_SETTLE: 'ip:settlement:settle',
    /**
     * Reverse a completed settlement. Separate from settling: unwinding an issued
     * legal document is a materially different authority from issuing one.
     */
    IP_SETTLEMENT_REVERSE: 'ip:settlement:reverse',
    // --- ADR-085 Phase 10 — finance posting ----------------------------------
    /** View posting evidence, verify journal fingerprints, run reconciliation. */
    IP_POSTING_VIEW: 'ip:posting:view',
    /** Post a settlement to the General Ledger. */
    IP_POSTING_POST: 'ip:posting:post',
    // --- ADR-085 Phase 11 — reporting ----------------------------------------
    //
    // Report ACCESS is gated here; each report ALSO declares the data permission
    // it needs, so a user who may read the discount register does not thereby
    // gain the finance reconciliation report.
    /** Open the reporting module and list available reports. */
    IP_REPORT_VIEW: 'ip:report:view',
    /**
     * Export to CSV/Excel/PDF. Separate from viewing: an export removes data
     * from the system's access controls entirely, and viewing does not.
     */
    IP_REPORT_EXPORT: 'ip:report:export',
    // --- ADR-085 Phase 12 — command centre ------------------------------------
    /**
     * Open the dashboard. Grants NO data: every card, alert and risk line
     * declares its own permission and is filtered OUT of the payload for a viewer
     * who lacks it — never greyed out with the number still in the response,
     * which would leak the figure to anyone reading it.
     */
    IP_DASHBOARD_VIEW: 'ip:dashboard:view',
    // --- ADR-085 Phase 13 — print & document engine ---------------------------
    /** Print a document for the first time. */
    IP_DOCUMENT_PRINT: 'ip:document:print',
    /**
     * The OFFICE copy of the bill as it stands now ("Final bill (office)", `POST /ip/documents/interim-bill`) —
     * a provisional sheet that carries charges saved but not yet approved, for office use and NOT for the
     * patient. Separate from `ip:document:print` on purpose: every desk that prints the discharge bill holds
     * that one, and a provisional total in a family's hands is how two different totals reach one patient.
     * Held by SuperAdmin only (owner, 2026-10-13: "office copy should be visible to Lathamani and super admin
     * only"; Lathamani, 6048, is a SuperAdmin).
     */
    IP_DOCUMENT_PRINT_OFFICE: 'ip:document:print-office',
    /**
     * Reprint. Separate from printing: a duplicate bill in circulation is how a
     * patient ends up paying twice, so reissuing one is its own authority.
     */
    IP_DOCUMENT_REPRINT: 'ip:document:reprint',
    /** View print history and the financial-evidence block. */
    IP_DOCUMENT_VIEW: 'ip:document:view',
    /**
     * PAYER CHANGEOVER (2026-09-28) — changing who pays for an existing stay.
     * Four grants, not one, because this reclassifies money that may already be
     * settled and posted, and asking for it, approving it and executing it are
     * three different authorities (the cancellation precedent above). Kept to
     * four on purpose: every permission is embedded in every JWT, and that has
     * already hit Node's and nginx's header limits twice.
     */
    /** Read changeovers and their analysis. */
    IP_CHANGEOVER_VIEW: 'ip:changeover:view',
    /** Raise a changeover, run its analysis, and cancel it before it is applied. */
    IP_CHANGEOVER_REQUEST: 'ip:changeover:request',
    /** Approve or reject an analysed changeover — a SEPARATE grant from asking for one. */
    IP_CHANGEOVER_APPROVE: 'ip:changeover:approve',
    /** Apply an approved changeover: rewrites allocations and, on a settled stay, reverses and re-posts it. */
    IP_CHANGEOVER_APPLY: 'ip:changeover:apply',
};
exports.ALL_IP_ADMISSION_PERMISSIONS = Object.values(exports.IpAdmissionPermissions);
//# sourceMappingURL=ip-admission.permissions.js.map